import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/api'
import MergePickerModal from './MergePickerModal'
import ReverseLookupPopup from '../deals/quote-assessment/ReverseLookupPopup'
import PhotoLightbox from '../shared/PhotoLightbox'
import '../../styles/pages.css'

/**
 * Gold-set labelling (identity-resolution eval, Prism
 * specs_20260920_identity_resolution_eval). A server-side sampler serves a
 * random draw of in-stock units (active, qty > 0, with photos, not yet
 * labelled), optionally one category. The operator identifies each unit's TRUE
 * model blind, from its photos, then confirms it against the catalogue or marks
 * it 'not in the catalogue'. The labels are what the resolver is graded on and
 * never touch the live KG.
 *
 * Blind by design: no system guess is shown, and the listing text is hidden.
 * Revealing the text or asking the AI records the label as not blind, so the
 * headline accuracy stays on the purely-blind ones.
 */

const ITEM_SOURCE = 'inventory'
const SAMPLE_SIZE = 8

function CatalogLabel() {
  const [category, setCategory] = useState('') // '' = any
  const [categories, setCategories] = useState([]) // [{ product_type, remaining }]
  const [pool, setPool] = useState([]) // [{ item_ref, title, vendor, product_type, image_urls }]
  const [idx, setIdx] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [chosen, setChosen] = useState(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [newMode, setNewMode] = useState(false)
  const [newBrand, setNewBrand] = useState('')
  const [newModel, setNewModel] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [recent, setRecent] = useState([])
  const [showText, setShowText] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [usedAssist, setUsedAssist] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const current = pool[idx] || null
  const imageUrls = current?.image_urls || []

  function resetIdentity() {
    setChosen(null)
    setNewMode(false)
    setNewBrand('')
    setNewModel('')
    setShowText(false)
    setAiOpen(false)
    setUsedAssist(false)
  }

  // Revealing the listing text or asking the AI is an assist: the label is
  // recorded as not blind, so the headline accuracy stays on the blind ones.
  function toggleText() {
    if (!showText) setUsedAssist(true)
    setShowText(s => !s)
  }

  function openAi() {
    setUsedAssist(true)
    setAiOpen(true)
  }

  // A fresh random draw from the server: in-stock units not yet labelled,
  // optionally one category. Randomness and exclusion are done server-side.
  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: String(SAMPLE_SIZE) })
      if (category) params.set('category', category)
      const units = await api.get(`/api/graph/gold-labels/inventory-sample?${params}`)
      setPool(units || [])
      setIdx(0)
      resetIdentity()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [category])

  useEffect(() => { load() }, [load])

  // The categories to choose from, loaded once.
  useEffect(() => {
    let alive = true
    api.get('/api/graph/gold-labels/inventory-categories')
      .then(cats => { if (alive) setCategories(cats || []) })
      .catch(() => { if (alive) setCategories([]) })
    return () => { alive = false }
  }, [])

  function advance() {
    if (idx + 1 < pool.length) {
      setIdx(idx + 1)
      resetIdentity()
    } else {
      load() // batch done -> fetch a fresh random draw
    }
  }

  function handlePick(item) {
    setChosen({ product_model_id: item.product_model_id, display_name: item.display_name })
    setNewMode(false)
    setPickerOpen(false)
  }

  const canSubmit =
    current &&
    (chosen || (newMode && newBrand.trim() && newModel.trim())) &&
    !submitting

  async function handleRecord() {
    if (!current) return
    setSubmitting(true)
    setError(null)
    const payload = newMode
      ? {
          item_source: ITEM_SOURCE,
          item_ref: current.item_ref,
          verdict: 'new',
          is_blind: !usedAssist,
          new_descriptor: { brand: newBrand.trim(), model: newModel.trim() }
        }
      : {
          item_source: ITEM_SOURCE,
          item_ref: current.item_ref,
          verdict: 'confirm',
          is_blind: !usedAssist,
          labeled_model_id: chosen.product_model_id
        }
    try {
      await api.post('/api/graph/gold-labels', payload)
      const name = newMode ? `New: ${newBrand.trim()} ${newModel.trim()}` : chosen.display_name
      setRecent(r => [{ title: current.title || current.item_ref, verdict: payload.verdict, name }, ...r].slice(0, 25))
      advance()
    } catch (err) {
      if (err.status === 409) {
        advance() // already labelled -> move on
      } else {
        setError(err.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)', maxWidth: 640 }}>
        Record the true identity of in-stock units, blind. Identify the item from its photos, then
        find the true model in the catalogue and confirm, or mark it as not in the catalogue. This is
        the ground truth the identity resolver is graded on. It never changes the live catalogue.
      </div>

      <div className="filter-group" style={{ maxWidth: 380, marginBottom: 'var(--space-4)' }}>
        <label className="filter-label">Category</label>
        <select className="form-input" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Any category</option>
          {groupByMain(categories).map(g => (
            <optgroup key={g.main} label={g.main}>
              {g.subs.map(c => (
                <option key={c.product_type} value={c.product_type}>
                  {c.product_type} ({c.remaining})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <div className="loading-spinner" />
        </div>
      ) : !current ? (
        <div className="empty-state">
          <div className="empty-state-title">Nothing to label</div>
          <div className="empty-state-description">
            No unlabelled in-stock units{category ? ' in this category' : ''}.
          </div>
          <button type="button" className="btn btn-secondary" style={{ marginTop: 'var(--space-3)' }} onClick={load}>
            Reload
          </button>
        </div>
      ) : (
        <>
          {/* The unit: photos first; the listing text (title, brand, category)
              is hidden so the label stays independent of text that can be wrong. */}
          <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            {imageUrls.length > 0 ? (
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
                {imageUrls.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`view ${i + 1}`}
                    title="Click to enlarge"
                    onClick={() => setLightboxIndex(i)}
                    style={{ width: 128, height: 128, objectFit: 'cover', borderRadius: 6, background: 'var(--color-bg-tertiary)', display: 'block', cursor: 'zoom-in' }}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-3)' }}>No photos on this unit.</div>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="text-sm text-muted" style={{ fontFamily: 'monospace' }}>{current.item_ref}</span>
              <button type="button" className="btn btn-sm" onClick={toggleText}>
                {showText ? 'Hide listing text' : 'Show listing text'}
              </button>
            </div>
            {showText ? (
              <div className="text-sm" style={{ marginTop: 'var(--space-2)' }}>
                <strong>{current.title || '(untitled)'}</strong>
                {current.vendor ? ` · ${current.vendor}` : ''}
                {current.product_type ? ` · ${current.product_type}` : ''}{' '}
                <span className="text-muted">(can be wrong)</span>
              </div>
            ) : (
              <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
                Identify the item from the photos. The listing title, brand and category are hidden to keep the label independent.
              </div>
            )}
          </div>

          {/* True identity */}
          <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <div className="filter-label" style={{ marginBottom: 'var(--space-3)' }}>True identity (blind)</div>
            {!newMode ? (
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                {chosen ? (
                  <>
                    <span className="badge badge-success">confirm</span>
                    <strong>{chosen.display_name}</strong>
                    <button type="button" className="btn btn-sm" onClick={() => setChosen(null)}>Change</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-secondary" onClick={() => setPickerOpen(true)}>
                    Find the model in the catalogue
                  </button>
                )}
                <span className="text-sm text-muted">or</span>
                <button type="button" className="btn btn-sm" onClick={() => { setNewMode(true); setChosen(null) }}>
                  Not in the catalogue (new)
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <span className="badge badge-neutral">new</span>
                  <span className="text-sm text-muted">The true model is not in the catalogue. Describe it.</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                  <div>
                    <div className="filter-label">Brand</div>
                    <input className="form-input" value={newBrand} onChange={e => setNewBrand(e.target.value)} placeholder="e.g. Marantz" />
                  </div>
                  <div>
                    <div className="filter-label">Model</div>
                    <input className="form-input" value={newModel} onChange={e => setNewModel(e.target.value)} placeholder="e.g. PM6006" />
                  </div>
                </div>
                <div>
                  <button type="button" className="btn btn-sm" onClick={() => { setNewMode(false); setNewBrand(''); setNewModel('') }}>
                    Back to catalogue search
                  </button>
                </div>
              </div>
            )}
            <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="text-sm text-muted">Stuck? An aid marks the label as not blind:</span>
              <button type="button" className="btn btn-sm" onClick={openAi}>Ask the AI</button>
              {usedAssist && <span className="badge badge-warning">assisted, not blind</span>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={handleRecord}>
              {submitting ? 'Recording…' : 'Record label'}
            </button>
            <button type="button" className="btn btn-sm" onClick={advance} disabled={submitting}>Skip</button>
            <span className="text-sm text-muted">{Math.max(0, pool.length - idx - 1)} more in this batch</span>
          </div>
        </>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <div className="filter-label" style={{ marginBottom: 'var(--space-2)' }}>Recorded this session ({recent.length})</div>
          <table className="table">
            <tbody>
              {recent.map((r, i) => (
                <tr key={`${r.title}-${i}`}>
                  <td style={{ width: 90 }}><span className={verdictBadge(r.verdict)}>{r.verdict}</span></td>
                  <td style={{ fontWeight: 500 }}>{r.title}</td>
                  <td className="text-sm text-muted">{r.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pickerOpen && (
        <MergePickerModal
          onPick={handlePick}
          onClose={() => setPickerOpen(false)}
          pickLabel="This is it"
          title="Find the true model"
        />
      )}

      {aiOpen && current && (
        <ReverseLookupPopup
          item={{ name: current.item_ref }}
          images={imageUrls}
          lookupPath="/api/graph/reverse-image-lookup"
          onClose={() => setAiOpen(false)}
        />
      )}

      {lightboxIndex !== null && (
        <PhotoLightbox
          images={imageUrls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}

// Group the (pre-ordered) category list under its KG main category, keeping
// the server's order; unknown types fall under "Other".
function groupByMain(cats) {
  const order = []
  const byMain = new Map()
  for (const c of cats) {
    const main = c.main || 'Other'
    if (!byMain.has(main)) {
      byMain.set(main, [])
      order.push(main)
    }
    byMain.get(main).push(c)
  }
  return order.map(main => ({ main, subs: byMain.get(main) }))
}

function verdictBadge(verdict) {
  if (verdict === 'confirm') return 'badge badge-success'
  if (verdict === 'new') return 'badge badge-neutral'
  return 'badge badge-warning'
}

export default CatalogLabel
